import React from 'react'
import { useSelector } from 'react-redux'
import { Stack } from '@mui/material'

import { type RootState } from '~redux/rootReducer'
import { useListTemplatesQuery } from '~redux/slices/apiSlice'
import { spacing } from '~tokens/internal'
import { type MessageTemplate0 } from '~types'
import { previewAIReplyPlacement } from '~utils/formLetterHtml'

import Dropdown, { type DropdownOption } from '../Dropdown'
import EmailPreview from '../EmailPreview'
import { ErrorText } from '../TextField'

const NO_FORM_LETTER_VALUE = ''

export interface FormLetterSelectorProps {
  organizationIdentityId?: string
  selectedFormLetterId: string | null
  onChange: (template: MessageTemplate0 | null) => void
  helperText?: string
  showAIReplyPlacement?: boolean
}

const FormLetterSelector: React.FC<FormLetterSelectorProps> = ({
  organizationIdentityId,
  selectedFormLetterId,
  onChange,
  helperText,
  showAIReplyPlacement = false,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const { data, isError } = useListTemplatesQuery(
    {
      user_id: userId as string,
      organization_identity_id_list: [organizationIdentityId as string],
    },
    { skip: !userId || !organizationIdentityId },
  )
  const templates: MessageTemplate0[] = data?.templates ?? []

  const options: DropdownOption[] = [
    { value: NO_FORM_LETTER_VALUE, label: 'None' },
    ...templates.map((template) => ({ value: template.id, label: template.name })),
  ]

  const selectedTemplate = templates.find((template) => template.id === selectedFormLetterId)

  const handleChange = (value: string | string[]): void => {
    const id = Array.isArray(value) ? value[0] : value
    onChange(templates.find((template) => template.id === id) ?? null)
  }

  return (
    <Stack spacing={spacing.xxs}>
      <Dropdown
        label='Form Letter'
        options={options}
        value={selectedFormLetterId ?? NO_FORM_LETTER_VALUE}
        onChange={handleChange}
        placeholder='Select a form letter'
        helperText={helperText}
        searchable
      />
      {isError && <ErrorText error='Failed to load form letters' />}
      {selectedTemplate && (
        <EmailPreview
          subject={selectedTemplate.name}
          body={
            showAIReplyPlacement
              ? previewAIReplyPlacement(selectedTemplate.body)
              : selectedTemplate.body
          }
        />
      )}
    </Stack>
  )
}

export default FormLetterSelector
